import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Paper, TableBody, TableCell, TableRow } from "@mui/material";
import styled from "@emotion/styled";

import Pagination from "Components/Common/BaseTable/Pagination";
import { usePagination } from "Components/Common/BaseTable";
import { StyledTable, StyledTableCell, StyledTableHead } from "./style";

const RECORDS_PER_PAGE = 10;
const emptyValues = [null, undefined, ""];

const StyledTableContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "maxHeight",
})`
  max-height: ${({ maxHeight }) => maxHeight ?? "unset"};
  overflow-y: auto;
  .MuiPaper-root {
    overflow-x: scroll;
  }
`;

const MaximumDemandConsumerWiseTable = ({ data, maxHeight, t }) => {
  const paginate = usePagination();
  const [rowData, setRowData] = useState([]);
  const { page, setTotalPages } = paginate;

  const allMonths = data[0]?.months?.map(({ months }) => months);

  const maximumDemandCategoryWise = useMemo(
    () =>
      rowData.reduce(
        (
          acc,
          { srNo, consumerNumber, consumerName, contractDemand, unit, months }
        ) => {
          if (!consumerNumber) {
            return acc;
          }

          const counts = months?.reduce(
            (counts, { contractLoad }, index) => ({
              ...counts,
              [`count${index}`]: emptyValues.includes(contractLoad)
                ? "--"
                : contractLoad,
            }),
            {}
          );

          return [
            ...acc,
            {
              srNo,
              consumerNumber,
              consumerName,
              contractDemand,
              unit,
              ...counts,
            },
          ];
        },
        []
      ),
    [rowData]
  );

  useEffect(() => {
    if (data?.length) {
      setTotalPages(Math.ceil(data.length / RECORDS_PER_PAGE));
    }
  }, [data]);

  useEffect(() => {
    const rowData =
      data?.slice(RECORDS_PER_PAGE * (page - 1), RECORDS_PER_PAGE * page) || [];
    setRowData(rowData);
  }, [page, data]);

  return (
    <>
      <StyledTableContainer maxHeight={maxHeight}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell rowSpan={2}>{t("Sr No.")}</StyledTableCell>
              <StyledTableCell rowSpan={2}>
                {t("consumer Number")}
              </StyledTableCell>
              <StyledTableCell rowSpan={2}>
                {t("consumer Name")}
              </StyledTableCell>
              <StyledTableCell rowSpan={2}>
                {t("contract Demand")}
              </StyledTableCell>
              <StyledTableCell rowSpan={2}>{t("Unit")}</StyledTableCell>
              {allMonths?.map((month, index) => (
                <React.Fragment key={index}>
                  <StyledTableCell align="center">{t(month)}</StyledTableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {maximumDemandCategoryWise.length ? (
              maximumDemandCategoryWise.map(
                (
                  {
                    srNo,
                    consumerNumber,
                    consumerName,
                    contractDemand,
                    unit,
                    ...rest
                  },
                  index
                ) => (
                  <TableRow key={index}>
                    <TableCell>{srNo}</TableCell>
                    <TableCell>{consumerNumber}</TableCell>
                    <TableCell>{consumerName}</TableCell>
                    <TableCell>{contractDemand}</TableCell>
                    <TableCell>{unit}</TableCell>
                    {Object.values(rest).map((count, index) => (
                      <TableCell key={index} align="center">
                        {count}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              )
            ) : (
              <TableRow>
                <TableCell colSpan={10} className="text-center">
                  {t("No Data Available")}!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      {!!maximumDemandCategoryWise.length && <Pagination paginate={paginate} />}
    </>
  );
};

MaximumDemandConsumerWiseTable.propTypes = {
  data: PropTypes.array,
  maxHeight: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(MaximumDemandConsumerWiseTable);
